<template>
  <transition name="cookie-slide">
    <aside 
      v-if="showBanner" 
      class="cookie-consent-wrapper" 
      role="alertdialog" 
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
      aria-live="polite"
    >
      <div class="cookie-consent-card glass-effect shadow-lg">
        
        <div class="cookie-content d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3">
          <!-- Icône décorative (ignorée par les lecteurs d'écran) -->
          <div class="cookie-icon flex-shrink-0" aria-hidden="true">
            <i class="fas fa-cookie-bite"></i>
          </div>

          <!-- Texte de consentement -->
          <div class="cookie-text flex-grow-1 text-center text-sm-start">
            <h4 id="cookie-title" class="cookie-title mb-1">Votre vie privée nous importe</h4>
            <!-- La balise <small> et le nofollow évitent que ce texte pollue le SEO de la page -->
            <p id="cookie-desc" class="mb-0 text-muted small">
              Nous utilisons des cookies pour assurer le bon fonctionnement du site, analyser notre trafic et améliorer votre expérience de commande. 
              En continuant, vous acceptez notre 
              <router-link to="/privacy-policy" class="privacy-link" rel="nofollow">
                politique de confidentialité
              </router-link>.
            </p>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="cookie-actions mt-3 d-flex flex-column flex-sm-row justify-content-end gap-2">
          <button 
            class="btn btn-sm cookie-btn cookie-btn-decline" 
            @click="declineCookies"
            aria-label="Refuser les cookies optionnels"
          >
            Continuer sans accepter
          </button>
          <button 
            class="btn btn-sm cookie-btn cookie-btn-accept" 
            @click="acceptCookies"
            aria-label="Accepter tous les cookies"
          >
            Tout accepter
          </button>
        </div>

      </div>
    </aside>
  </transition>
</template>

<script>
export default {
  name: 'CookieConsent',
  data() {
    return {
      showBanner: false,
    };
  },
  mounted() {
    setTimeout(() => {
      const storedConsent = localStorage.getItem('cookieConsent');
      if (storedConsent === null) {
        this.showBanner = true;
      } else if (storedConsent === 'true') {
        this.enableTracking();
      }
    }, 1500);
  },
  methods: {
    acceptCookies() {
      this.showBanner = false;
      localStorage.setItem('cookieConsent', 'true');
      this.enableTracking();
    },
    declineCookies() {
      this.showBanner = false;
      localStorage.setItem('cookieConsent', 'false');
    },
    enableTracking() {
      if (window.fbq) {
        window.fbq('consent', 'grant');
      }
      
      if (typeof window.gtag === 'function') {
        // eslint-disable-next-line no-undef
        gtag('consent', 'update', {
          'analytics_storage': 'granted'
        });
      }
    }
  },
};
</script>

<style scoped>
/* ==========================================================================
   Positionnement Flottant (Évite le layout shift - CLS - pénalisé par Google)
   ========================================================================== */
.cookie-consent-wrapper {
  position: fixed;
  bottom: 25px;
  left: 25px;
  z-index: 1050; /* Au-dessus de la navbar (1030) */
  max-width: 440px;
  width: calc(100% - 50px);
}

/* ==========================================================================
   Design de la Carte (Glassmorphism Pizzeria)
   ========================================================================== */
.cookie-consent-card {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(211, 47, 47, 0.15);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
}

/* ==========================================================================
   Contenu & Typographie
   ========================================================================== */
.cookie-icon {
  font-size: 1.8rem;
  color: #d32f2f;
  background: rgba(211, 47, 47, 0.08);
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(211, 47, 47, 0.1);
}

.cookie-title {
  font-family: 'Merriweather', serif;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a1a1a;
}

.privacy-link {
  color: #d32f2f;
  text-decoration: none;
  font-weight: 700;
  position: relative;
  transition: color 0.3s ease;
}

.privacy-link:hover {
  color: #a62626;
  text-decoration: underline;
}

/* ==========================================================================
   Boutons
   ========================================================================== */
.cookie-btn {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  transition: all 0.3s ease;
}

.cookie-btn-accept {
  background-color: #d32f2f;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.2);
}

.cookie-btn-accept:hover {
  background-color: #a62626;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(211, 47, 47, 0.3);
}

.cookie-btn-decline {
  background-color: transparent;
  color: #555;
  border: 1px solid rgba(108, 117, 125, 0.4);
}

.cookie-btn-decline:hover {
  background-color: rgba(108, 117, 125, 0.08);
  color: #222;
}

/* ==========================================================================
   Animations Vue (Fluides et rapides pour UX)
   ========================================================================== */
.cookie-slide-enter-active {
  transition: all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.cookie-slide-leave-active {
  transition: all 0.4s ease-in-out;
}

.cookie-slide-enter-from {
  opacity: 0;
  transform: translateY(40px) scale(0.95);
}

.cookie-slide-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* ==========================================================================
   Responsive Mobile
   ========================================================================== */
@media (max-width: 575px) {
  .cookie-consent-wrapper {
    bottom: 15px;
    left: 15px;
    width: calc(100% - 30px);
    max-width: none;
  }
  
  .cookie-actions {
    flex-direction: column-reverse !important;
  }

  .cookie-btn {
    width: 100%;
    padding: 0.8rem;
    font-size: 0.95rem;
  }
  
  .cookie-icon {
    display: none;
  }
  
  .cookie-content {
    text-align: center;
  }
}
</style>